/**
 * ActivityTimeline - Visual timeline showing daily activity periods
 */

import React, { useState } from 'react';
import { DailyActivity } from '../types/bureau-activity.types';
import type { TodoCountEntry } from '../api/bureau-activity.api';

interface UserPlanning {
  dayPlanning: { start: string; end: string; enabled: boolean; isRemote: boolean } | null;
  appointments: { start: string; end: string; title: string }[];
}

interface ActivityTimelineProps {
  activities: DailyActivity[];
  selectedUserId?: string;
  onUserSelect?: (userId: string) => void;
  getPlanningForUserDate?: (userId: string, date: string) => UserPlanning;
  todoCounts?: Record<string, TodoCountEntry>;
}

// Format time string to HH:MM
const formatTime = (timestamp: string | null): string => {
  if (!timestamp) return '--:--';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
};

// Format decimal hours to Xh or XhYY (e.g., 2.75 -> "2h45", 0.5 -> "30min")
const formatDuration = (decimalHours: string | number): string => {
  const hours = typeof decimalHours === 'string' ? parseFloat(decimalHours) : decimalHours;
  if (isNaN(hours) || hours === 0) return '0min';

  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, '0')}`;
};

// Format date for display
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-BE', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Convert time to position on 24h timeline (0-100%)
const timeToPosition = (timestamp: string | null): number => {
  if (!timestamp) return 0;
  const date = new Date(timestamp);
  const hours = date.getHours() + date.getMinutes() / 60;
  return (hours / 24) * 100;
};

// Convert HH:MM string to position on 24h timeline (0-100%)
const hhmmToPosition = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  const hours = h + m / 60;
  return (hours / 24) * 100;
};

// Get color based on activity intensity
const getIntensityColor = (requestsPerHour: number): string => {
  if (requestsPerHour > 100) return 'bg-green-600';
  if (requestsPerHour > 50) return 'bg-green-500';
  if (requestsPerHour > 20) return 'bg-yellow-500';
  if (requestsPerHour > 10) return 'bg-orange-500';
  return 'bg-red-500';
};

// Group activities by user
const groupByUser = (activities: DailyActivity[]): Map<string, DailyActivity[]> => {
  const grouped = new Map<string, DailyActivity[]>();
  activities.forEach(activity => {
    const existing = grouped.get(activity.userId) || [];
    existing.push(activity);
    grouped.set(activity.userId, existing);
  });
  return grouped;
};

// Activity bar component with mouse-following tooltip
const ActivityBar: React.FC<{
  activity: DailyActivity;
  startPos: number;
  width: number;
  intensity: number;
}> = ({ activity, startPos, width, intensity }) => {
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <div
        className={`absolute h-full rounded ${getIntensityColor(intensity)} opacity-80 hover:opacity-100 transition-opacity cursor-pointer z-10`}
        style={{
          left: `${startPos}%`,
          width: `${width}%`
        }}
        onMouseEnter={handleMouseMove}
        onMouseLeave={() => setTooltipPos(null)}
        onMouseMove={handleMouseMove}
      />
      {/* Fixed tooltip that follows cursor */}
      {tooltipPos && (
        <div
          className="fixed flex gap-2 text-[10px] font-semibold pointer-events-none z-50"
          style={{
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y - 35}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <span className="bg-gray-900/90 text-white px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap">
            {formatTime(activity.firstActivity)} - {formatTime(activity.lastActivity)}
          </span>
          <span className="bg-gray-900/90 text-white px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap">
            {activity.totalRequests} req
          </span>
          <span className="bg-gray-900/90 text-white px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap">
            {formatDuration(activity.activeHours)} actif
          </span>
        </div>
      )}
    </>
  );
};

// Group activities by date
const groupByDate = (activities: DailyActivity[]): Map<string, DailyActivity[]> => {
  const grouped = new Map<string, DailyActivity[]>();
  activities.forEach(activity => {
    const existing = grouped.get(activity.activityDate) || [];
    existing.push(activity);
    grouped.set(activity.activityDate, existing);
  });
  // Sort by date descending
  return new Map([...grouped.entries()].sort((a, b) => b[0].localeCompare(a[0])));
};

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  activities,
  selectedUserId,
  onUserSelect,
  getPlanningForUserDate,
  todoCounts
}) => {
  // Filter by selected user if any
  const filteredActivities = selectedUserId
    ? activities.filter(a => a.userId === selectedUserId)
    : activities;

  // Group by date for display
  const groupedByDate = groupByDate(filteredActivities);

  // Hour markers for the timeline
  const hourMarkers = [0, 6, 9, 12, 15, 18, 21, 24];

  if (filteredActivities.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Aucune activité trouvée pour cette période
      </div>
    );
  }

  return (
    <div className="activity-timeline">
      {/* Timeline header with hour markers */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200 pb-2 mb-4">
        <div className="flex items-center ml-48">
          <div className="flex-1 relative h-6">
            {hourMarkers.map(hour => (
              <div
                key={hour}
                className="absolute text-xs text-gray-500"
                style={{ left: `${(hour / 24) * 100}%`, transform: 'translateX(-50%)' }}
              >
                {hour}h
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activities grouped by date */}
      {Array.from(groupedByDate.entries()).map(([date, dayActivities]) => (
        <div key={date} className="mb-6">
          {/* Date header */}
          <div className="text-sm font-semibold text-gray-700 mb-2">
            {formatDate(date)}
          </div>

          {/* User rows for this date */}
          {dayActivities.map(activity => {
            const startPos = timeToPosition(activity.firstActivity);
            const endPos = timeToPosition(activity.lastActivity);
            const width = Math.max(endPos - startPos, 2); // Minimum 2% width for visibility
            const intensity = parseFloat(activity.requestsPerHour);

            // Get planning data for this user on this date
            const planning = getPlanningForUserDate?.(activity.userId, date);
            const dayPlanning = planning?.dayPlanning;
            const appointments = planning?.appointments || [];

            // Calculate available work blocks (work hours minus breaks/appointments)
            const getAvailableBlocks = () => {
              if (!dayPlanning) return [];

              // Start with the full work period
              const workStart = dayPlanning.start;
              const workEnd = dayPlanning.end;

              // Sort appointments by start time
              const sortedAppts = [...appointments].sort((a, b) => a.start.localeCompare(b.start));

              // Split work period around appointments
              const blocks: { start: string; end: string }[] = [];
              let currentStart = workStart;

              for (const appt of sortedAppts) {
                // If appointment starts after current position, add a block
                if (appt.start > currentStart && appt.start < workEnd) {
                  if (currentStart < appt.start) {
                    blocks.push({ start: currentStart, end: appt.start });
                  }
                  // Move current position to after appointment
                  currentStart = appt.end > currentStart ? appt.end : currentStart;
                }
              }

              // Add final block if there's time remaining
              if (currentStart < workEnd) {
                blocks.push({ start: currentStart, end: workEnd });
              }

              return blocks;
            };

            const availableBlocks = getAvailableBlocks();

            return (
              <div
                key={`${activity.userId}-${date}`}
                className="flex items-center mb-2 group"
              >
                {/* User name - clickable to filter */}
                <div className="w-72 pr-4 truncate flex items-center gap-1.5">
                  <button
                    onClick={() => onUserSelect?.(activity.userId)}
                    className="text-sm font-medium text-gray-800 hover:text-blue-600 hover:underline transition-colors text-left truncate"
                    title={`Cliquez pour voir uniquement ${activity.userName}`}
                  >
                    {activity.userName}
                  </button>
                  {todoCounts && todoCounts[activity.userId] && (
                    <span className="inline-flex items-center gap-2 text-xs whitespace-nowrap ml-1">
                      <span
                        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full font-bold text-white ${
                          (todoCounts[activity.userId]?.open ?? 0) > 20 ? 'bg-red-500' :
                          (todoCounts[activity.userId]?.open ?? 0) > 5 ? 'bg-orange-500' :
                          (todoCounts[activity.userId]?.open ?? 0) > 0 ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                        title="À faire"
                      >
                        {todoCounts[activity.userId]?.open ?? 0}
                      </span>
                      <span className="text-green-600 font-medium" title="Traités dernières 24h">
                        {todoCounts[activity.userId]?.done_1d ?? 0}
                        <span className="text-gray-400 ml-0.5">/24h</span>
                      </span>
                      <span className="text-green-700 font-medium" title="Traités 7 derniers jours">
                        {todoCounts[activity.userId]?.done_7d ?? 0}
                        <span className="text-gray-400 ml-0.5">/7j</span>
                      </span>
                    </span>
                  )}
                </div>

                {/* Timeline container */}
                <div className="flex-1 flex flex-col gap-0.5">
                  {/* Activity bar */}
                  <div className="relative h-7 bg-gray-100 rounded overflow-hidden">
                    {/* Activity bar */}
                    {activity.firstActivity && activity.lastActivity && (
                      <>
                        <ActivityBar
                          activity={activity}
                          startPos={startPos}
                          width={width}
                          intensity={intensity}
                        />
                        {/* Inactivity gaps (> 10 min) - red blocks - clipped to activity bar bounds */}
                        {activity.inactivityGaps?.map((gap, idx) => {
                          const gapStart = hhmmToPosition(gap.start);
                          const gapEnd = hhmmToPosition(gap.end);
                          const activityEnd = startPos + width;

                          // Only show gaps that overlap with the activity bar
                          if (gapEnd <= startPos || gapStart >= activityEnd) return null;

                          // Clip gap to activity bar bounds
                          const clippedStart = Math.max(gapStart, startPos);
                          const clippedEnd = Math.min(gapEnd, activityEnd);
                          const clippedWidth = clippedEnd - clippedStart;

                          if (clippedWidth <= 0) return null;

                          return (
                            <div
                              key={`gap-${idx}`}
                              className="absolute h-full bg-red-400/70 z-20"
                              style={{
                                left: `${clippedStart}%`,
                                width: `${clippedWidth}%`
                              }}
                              title={`Inactif: ${gap.start} - ${gap.end} (${gap.minutes} min)`}
                            />
                          );
                        })}
                      </>
                    )}

                    {/* Hour gridlines */}
                    {hourMarkers.slice(1, -1).map(hour => (
                      <div
                        key={hour}
                        className="absolute top-0 bottom-0 w-px bg-gray-300"
                        style={{ left: `${(hour / 24) * 100}%` }}
                      />
                    ))}
                  </div>

                  {/* Working hours thin line */}
                  <div className="relative h-1.5 rounded-full overflow-hidden">
                    {availableBlocks.map((block, idx) => (
                      <div
                        key={idx}
                        className={`absolute h-full rounded-full ${dayPlanning?.isRemote ? 'bg-purple-400' : 'bg-blue-400'}`}
                        style={{
                          left: `${hhmmToPosition(block.start)}%`,
                          width: `${hhmmToPosition(block.end) - hhmmToPosition(block.start)}%`
                        }}
                        title={`Horaire: ${block.start} - ${block.end}${dayPlanning?.isRemote ? ' (télétravail)' : ''}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Stats summary */}
                <div className="w-32 pl-4 text-xs text-gray-600 text-right">
                  <span className="font-medium">{activity.totalRequests}</span> req
                  <span className="mx-1">|</span>
                  <span className="font-medium">{formatDuration(activity.activeHours)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        {/* Availability legend */}
        <div className="text-xs text-gray-500 mb-2">Disponibilité:</div>
        <div className="flex items-center gap-4 text-xs mb-4">
          <div className="flex items-center gap-1">
            <div className="w-6 h-1.5 rounded-full bg-blue-400" />
            <span>Horaire bureau</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-1.5 rounded-full bg-purple-400" />
            <span>Télétravail</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-4 rounded bg-red-400/60" />
            <span>Inactif (&gt;10 min)</span>
          </div>
        </div>

        {/* Intensity legend */}
        <div className="text-xs text-gray-500 mb-2">Intensité (requêtes/heure):</div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-500" />
            <span>&lt;10</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-orange-500" />
            <span>10-20</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-yellow-500" />
            <span>20-50</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span>50-100</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-600" />
            <span>&gt;100</span>
          </div>
        </div>
      </div>
    </div>
  );
};
